/**
 * Object **movement** contributions — the movement-side twin of `objectContributions.ts`. That
 * sibling derives `blocksSightCells`/`softCoverCells`/`hardCoverCells` for `terrainVisibility`;
 * this module derives the analogous per-cell **`moveCost`** contribution for `movement.ts`'s
 * `movementContext` (the `terrainMoveCost` seam) — same source (placed, live, non-Destroyed
 * `object`/`deployable` footprints × `objectProfile`), different closure.
 *
 * **Occupancy (`blocksMovement`) is NOT this module's concern** — that's honored directly in
 * `movement.ts`, where a live object without `blocksMovement: true` exits the `blocked`/`noStop`
 * classification entirely. This module only answers "how much does it cost to enter this
 * footprint", which applies **even to a passable object** — a wreck is difficult terrain precisely
 * because it doesn't block movement but still costs extra to cross.
 *
 * **Destroyed objects contribute nothing** — same posture as the sight/cover twin: cost is a live
 * query (nothing caches it on `EncounterState`), so destruction takes effect on the very next
 * `movementContext` call, no invalidation needed.
 */
import type { Space } from "@lancer-vtt/space";
import type { Cell, Combatant, CombatantId } from "./types";
import { contributesToTerrain, hasStatus } from "./types";

/**
 * A placed, live (non-Destroyed) object/deployable combatant carrying a numeric `moveCost` —
 * gated through {@link contributesToTerrain} rather than a kind check; `objectProfile` (and so
 * `moveCost`) is exclusive to `object`/`deployable` kinds by construction, so the capability
 * presence check subsumes a kind check.
 */
function liveMoveCostObjects(combatants: Record<CombatantId, Combatant>): Combatant[] {
  return Object.values(combatants).filter(
    (c) => contributesToTerrain(c) && c.pos != null && typeof c.base.objectProfile?.moveCost === "number" && !hasStatus(c.statuses, "Destroyed"),
  );
}

/**
 * The per-cell `moveCost` contribution of every placed, live object/deployable in
 * `combatants` — a sparse `Map<cellKey, cost>` the caller folds into its own
 * terrain-derived cost via `Math.max` (`movement.ts`'s `movementContext`): an
 * object's cost only ever ADDS difficulty on top of terrain, it never lowers it —
 * the same "worse wins" posture `visibility()` uses for hard-outranks-soft cover.
 * Two overlapping objects' costs at the same cell also fold by `max` here
 * (deliberately — the highest quoted cost governs, not whichever was iterated
 * last).
 */
export function objectMoveCostCells(space: Space<Cell>, combatants: Record<CombatantId, Combatant>): Map<string, number> {
  const out = new Map<string, number>();
  for (const c of liveMoveCostObjects(combatants)) {
    const cost = c.base.objectProfile!.moveCost!;
    for (const cell of space.footprint(c.pos!, c.base.size).cells()) {
      const key = space.key(cell);
      const existing = out.get(key);
      if (existing === undefined || cost > existing) out.set(key, cost);
    }
  }
  return out;
}
